import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { motion } from 'framer-motion';
import { 
  CheckIcon, 
  ShieldCheckIcon, 
  CreditCardIcon, 
  ChartBarIcon,
  IdentificationIcon,
  AcademicCapIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/solid';
import { UsersIcon, StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext'; 
import { applicationsAPI, categoriesAPI } from '../utils/api';
import toast from 'react-hot-toast'; 

const BecomeSmartPage = () => {
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Champs attendus par la collection `candidatures`, partagée avec l'appli.
  // Le nom, l'email et le téléphone viennent du profil : ils ne sont pas ressaisis.
  const [applicationForm, setApplicationForm] = useState({
    type: 'smart',
    profession: '',
    categorie: '',
    experience: '',
    competences: '',
    zone: '',
    bio: ''
  });
  const [categories, setCategories] = useState([]);
  const [submittingApp, setSubmittingApp] = useState(false);

  // Les 34 catégories sont servies par l'API, qui les tient de l'application
  useEffect(() => {
    categoriesAPI
      .getAll()
      .then(({ data }) => setCategories(data))
      .catch(() => setCategories([]));
  }, []);

  // Le reste des states reste identique
  const [activePlan, setActivePlan] = useState('pro'); // Garder cette ligne

  const benefits = [
    'Accès à des milliers de clients',
    'Gestion d\'agenda intégrée',
    'Paiements 100% sécurisés',
    'Profil vérifié et certifié',
    'Tableau de bord analytique',
    'Support prioritaire 7j/7',
  ];

  const steps = [
    { number: '01', title: 'Remplissez le formulaire', desc: 'Soumettez votre candidature avec votre métier et compétences' },
    { number: '02', title: 'Vérification de profil', desc: 'Notre équipe vérifie votre identité sous 48h' },
    { number: '03', title: 'Configurez votre profil', desc: 'Ajoutez vos photos, services et tarifs' },
    { number: '04', title: 'Commencez à gagner', desc: 'Recevez vos premières demandes et développez votre activité' },
  ];

  const stats = [
    { value: '250+', label: 'Smarts actifs', icon: UsersIcon },
    { value: '4.8★', label: 'Note moyenne', icon: StarOutlineIcon },
    { value: '20+', label: 'Catégories métiers', icon: ArrowTrendingUpIcon },
    { value: '0 FCFA', label: 'Inscription gratuite', icon: SparklesIcon },
  ];

  const conditions = [
    {
      icon: IdentificationIcon,
      title: 'Pièce d\'identité valide',
      description: 'CNI, passeport ou toute pièce officielle togolaise ou étrangère reconnue.'
    },
    {
      icon: AcademicCapIcon,
      title: 'Compétence prouvable',
      description: 'Diplôme, certification, portfolio ou références de clients précédents dans votre domaine.'
    },
    {
      icon: MapPinIcon,
      title: 'Basé en Afrique de l\'Ouest',
      description: 'Actuellement disponible à Lomé. Extension en cours dans d\'autres villes et pays.'
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Smartphone compatible',
      description: 'iOS 12+ ou Android 8+ pour accéder à l\'application Smart VADOM.'
    },
    {
      icon: StarIcon,
      title: 'Engagement qualité',
      description: 'Maintenir une note minimale de 3.5/5 pour rester actif sur la plateforme.'
    },
    {
      icon: CreditCardIcon,
      title: 'Numéro mobile money',
      description: 'Compte mobile money actif (Flooz, T-Money ou autre) pour recevoir vos paiements.'
    }
  ];

  const plans = [
    {
      id: 'starter',
      name: 'STARTER',
      price: 'Gratuit',
      period: '/ mois',
      description: 'Pour débuter et tester la plateforme sans risque. Idéal pour les débutants.',
      features: [
        'Profil de base',
        '5 réservations/mois',
        'Paiements sécurisés',
        'Support email'
      ],
      buttonText: 'Commencer gratuit',
      buttonClass: 'btn-outline',
      popular: false,
      icon: RocketLaunchIcon
    },
    {
      id: 'pro',
      name: 'SMART PRO',
      price: '5000',
      currency: 'FCFA',
      period: '/ mois',
      description: 'Pour les prestataires sérieux qui veulent développer leur clientèle rapidement.',
      features: [
        'Profil premium mis en avant',
        'Réservations illimitées',
        'Paiements prioritaires',
        'Analytics complets',
        'Badge Smart vérifié',
        'Support prioritaire 7j/7'
      ],
      buttonText: 'Choisir Smart Pro',
      buttonClass: 'btn-primary',
      popular: true,
      icon: StarIcon
    },
    {
      id: 'entreprise',
      name: 'POPULAIRE',
      price: 'Entreprise',
      period: 'Sur devis',
      description: 'Pour les entreprises et agences qui souhaitent référencer plusieurs prestataires.',
      features: [
        'Gestion d\'équipe',
        'Tableau de bord multi-profil',
        'API d\'intégration',
        'Account manager dédié',
        'Facturation mensuelle',
        'SLA garanti'
      ],
      buttonText: 'Nous contacter',
      buttonClass: 'btn-outline',
      popular: false,
      icon: BuildingOfficeIcon
    }
  ];

  // Fonction de soumission du formulaire
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      toast.error('Veuillez vous connecter pour soumettre votre candidature');
      navigate('/login');
      return;
    }
    
    setSubmittingApp(true);
    
    try {
      await applicationsAPI.submit(applicationForm);
      toast.success('Candidature envoyée avec succès ! Notre équipe vous répondra sous 48h.');
      setApplicationForm({
        type: 'smart',
        profession: '',
        categorie: '',
        experience: '',
        competences: '',
        zone: '',
        bio: ''
      });
    } catch (error) {
      console.error('Erreur candidature:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de votre candidature');
    } finally {
      setSubmittingApp(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section - inchangée */}
      <section className="relative bg-gradient-to-br from-[#7c2d12] to-[#ea580c] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/50 rounded-full -translate-y-1/2 translate-x-1/4 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/50 rounded-full translate-y-1/2 -translate-x-1/4 animate-pulse" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <span className="text-sm font-medium">Rejoignez l'élite VADOM</span>
            </motion.div>
            
            <h1 className="text-4xl lg:text-6xl font-display font-bold mb-6 leading-tight">
              Devenez un{' '}
              <span className="bg-gradient-to-r from-white to-orange-200 bg-clip-text text-transparent">
                Smart VADOM
              </span>{' '}
              et boostez votre activité
            </h1>
            <p className="text-xl text-white/90 mb-10 max-w-2xl">
              Rejoignez notre réseau de prestataires d'élite. Accédez à des milliers de clients,
              gérez votre agenda et encaissez en toute sécurité.
            </p>
            <motion.button 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => document.getElementById('candidature')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-white text-[#F97316] px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl transition-all duration-300"
                        >
                          Commencer maintenant
                        </motion.button>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                  fill="white"/>
          </svg>
        </div>
      </section>

      {/* Section Définition - inchangée */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#F97316] text-sm font-medium">Définition</span>
            </div>
            <h2 className="section-title">
              Qu'est-ce qu'un <span className="text-[#F97316]">Smart VADOM</span> ?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-[#AAAAAA] text-lg leading-relaxed">
                Un <span className="text-[#F97316] font-semibold">Smart</span> est un prestataire de services référencé, 
                vérifié et mis en avant sur la plateforme <span className="text-[#F97316] font-semibold">VADOM</span>. 
                C'est le terme que nous utilisons pour désigner nos professionnels d'élite.
              </p>
              <p className="text-[#AAAAAA] text-lg leading-relaxed">
                Artisan, technicien, livreur, professionnel indépendant — si vous avez une compétence à offrir, 
                VADOM est votre vitrine. Votre profil vérifié, vos évaluations clients et vos options de paiement 
                intégrées garantissent votre crédibilité.
              </p>
              
              <div className="space-y-4 mt-8">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="h-6 w-6 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold">Profil vérifié et certifié</h3>
                    <p className="text-[#AAAAAA]">Badge de confiance visible par tous les clients sur la plateforme.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCardIcon className="h-6 w-6 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold">Paiements sécurisés</h3>
                    <p className="text-[#AAAAAA]">Encaissiez via mobile money, carte ou espèces avec garantie de paiement.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ChartBarIcon className="h-6 w-6 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold">Tableau de bord analytique</h3>
                    <p className="text-[#AAAAAA]">Suivez vos performances, réservations et revenus en temps réel.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#111111] rounded-2xl p-6 text-center border border-[#252525] hover:border-[#F97316]/30 transition-all duration-300"
                >
                  <stat.icon className="h-8 w-8 text-[#F97316] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-[#AAAAAA]">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Conditions - inchangée */}
      <section className="py-20 bg-[#111111]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#F97316] text-sm font-medium">Conditions</span>
            </div>
            <h2 className="section-title">
              Ce qu'on attend de nos <span className="text-[#F97316]">Smarts</span>
            </h2>
            <p className="text-[#AAAAAA] mt-4 max-w-2xl mx-auto">
              Pour garantir la qualité du service et la confiance de notre communauté, 
              voici les critères pour rejoindre le réseau VADOM.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.map((condition, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-[#0a0a0a] rounded-2xl p-6 border border-[#252525] hover:border-[#F97316]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#F97316]/20 transition-colors">
                  <condition.icon className="h-6 w-6 text-[#F97316]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{condition.title}</h3>
                <p className="text-[#AAAAAA] text-sm leading-relaxed">{condition.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Tarification - inchangée */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#F97316] text-sm font-medium">Tarification</span>
            </div>
            <h2 className="section-title">
              Des offres adaptées à <span className="text-[#F97316]">chaque niveau d'activité</span>
            </h2>
            <p className="text-[#AAAAAA] mt-4 max-w-2xl mx-auto">
              Commencez gratuitement. Passez à un plan premium pour plus de visibilité et de fonctionnalités.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className={`relative bg-[#111111] rounded-2xl overflow-hidden border transition-all duration-300
                  ${plan.popular 
                    ? 'border-[#F97316] shadow-xl shadow-[#F97316]/10 scale-105 md:scale-110 z-10' 
                    : 'border-[#252525] hover:border-[#F97316]/30'}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-[#F97316] text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                      POPULAIRE
                    </div>
                  </div>
                )}
                
                <div className="p-8">
                  <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center mb-4">
                    <plan.icon className="h-6 w-6 text-[#F97316]" />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#F97316]">{plan.price}</span>
                    {plan.currency && <span className="text-lg text-white"> {plan.currency}</span>}
                    <span className="text-[#AAAAAA] text-sm">{plan.period}</span>
                  </div>
                  <p className="text-[#AAAAAA] text-sm mb-6">{plan.description}</p>
                  
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4 text-[#F97316]" />
                        <span className="text-sm text-[#CCCCCC]">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => document.getElementById('candidature')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300
                      ${plan.buttonClass === 'btn-primary' 
                        ? 'bg-[#F97316] text-white hover:bg-[#e8630a] shadow-lg shadow-[#F97316]/25' 
                        : 'bg-[#252525] text-white hover:bg-[#333333] border border-[#333333]'}`}
                  >
                    {plan.buttonText}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages - inchangé */}
      <section className="py-20 bg-[#111111]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">
              Des avantages conçus pour <span className="text-[#F97316]">propulser votre activité</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ x: 5 }}
                className="flex items-center gap-4 bg-[#0a0a0a] rounded-xl p-4 border border-[#252525]"
              >
                <div className="w-8 h-8 bg-[#F97316]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckIcon className="h-4 w-4 text-[#F97316]" />
                </div>
                <span className="text-[#CCCCCC] font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Étapes - inchangée */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">
              Comment devenir Smart en <span className="text-[#F97316]">4 étapes</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="relative text-center bg-[#111111] rounded-2xl p-6 border border-[#252525]"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#F97316] to-[#e8630a] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-[#AAAAAA] text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/*Formulaire candidature avec gestion d'état */}
      <section id="candidature" className="py-20 bg-gradient-to-b from-[#111111] to-[#0a0a0a]">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="section-title">
              Prêt à rejoindre les <span className="text-[#F97316]">Smarts</span> ?
            </h2>
            <p className="text-[#AAAAAA]">Remplissez le formulaire ci-dessous et notre équipe vous contactera sous 48h</p>
          </motion.div>
          
          <motion.form 
            onSubmit={handleSubmitApplication}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-[#111111] rounded-2xl p-8 border border-[#252525] space-y-6"
          >
            {/* Identité reprise du profil : ce sont les valeurs que verra
                l'administrateur, elles ne sont pas ressaisies ici */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Nom complet</label>
                <input
                  type="text"
                  value={user?.nom || ''}
                  readOnly
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-[#AAAAAA] cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-[#AAAAAA] cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Téléphone</label>
              <input
                type="tel"
                value={user?.telephone || ''}
                readOnly
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-[#AAAAAA] cursor-not-allowed"
              />
              <p className="text-xs text-[#666666] mt-2">
                Ces informations proviennent de votre compte VADOM. Modifiez-les depuis votre profil.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Type de candidature</label>
              <div className="grid grid-cols-2 gap-3">
                {[{ id: 'smart', label: 'Smart (prestataire)' }, { id: 'livreur', label: 'Livreur' }].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setApplicationForm({ ...applicationForm, type: option.id })}
                    className={`py-3 rounded-xl font-medium transition-all ${
                      applicationForm.type === option.id
                        ? 'bg-[#F97316] text-white shadow-lg shadow-[#F97316]/25'
                        : 'bg-[#0a0a0a] text-[#AAAAAA] border border-[#252525] hover:bg-[#252525]'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Votre métier</label>
              <select 
                value={applicationForm.profession}
                onChange={(e) => setApplicationForm({...applicationForm, profession: e.target.value})}
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all"
                required
              >
                <option value="">Sélectionnez votre métier</option>
                <option>Plombier</option>
                <option>Électricien</option>
                <option>Menuisier</option>
                <option>Coiffeur/Coiffeuse</option>
                <option>Jardinier</option>
                <option>Livreur</option>
                <option>Développeur</option>
                <option>Designer</option>
                <option>Autre</option>
              </select>
            </div>
            
            {/* La catégorie classe la fiche dans l'application mobile :
                sans elle, le Smart n'apparaît dans aucun filtre */}
            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Catégorie</label>
              <select
                value={applicationForm.categorie}
                onChange={(e) => setApplicationForm({ ...applicationForm, categorie: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all"
                required
              >
                <option value="">Sélectionnez une catégorie</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Zone d'intervention</label>
              <input
                type="text"
                value={applicationForm.zone}
                onChange={(e) => setApplicationForm({ ...applicationForm, zone: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all"
                placeholder="Ex : Tokoin, Lomé"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Compétences (séparées par des virgules)</label>
              <input 
                type="text"
                value={applicationForm.competences}
                onChange={(e) => setApplicationForm({...applicationForm, competences: e.target.value})}
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all"
                placeholder="Ex: Plomberie, dépannage, installation"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Description de votre expérience</label>
              <textarea 
                rows={4} 
                value={applicationForm.experience}
                onChange={(e) => setApplicationForm({...applicationForm, experience: e.target.value})}
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all resize-none" 
                placeholder="Décrivez votre expérience professionnelle..."
                required
              />
            </div>
            
            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit" 
              disabled={submittingApp}
              className="btn-primary w-full disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submittingApp ? 'Envoi en cours...' : 'Soumettre ma candidature'}
            </motion.button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default BecomeSmartPage;